import { useCallback, useEffect, useState } from 'react';
import track from '@src/utils/sensor/track';
const RETRY_EVENT = 'RETRY_TRIGGERED';
// max allowed retries
const RETRY_LIMIT = 4;
// wait time before retrying in ms per attempt count
const AUTO_RETRY_DELAYS = {
  0: 5000,
  1: 8000,
};
const AUTO_RETRY_LIMIT = 2;

const getAutoRetryDelay = (attempt) => AUTO_RETRY_DELAYS[attempt] || AUTO_RETRY_DELAYS[1];
const logEvent = () => track({ event: RETRY_EVENT });

// retry hook
// fetchData should be wrapped in dispatch
function useRetry({ fetchData, auto = false, hasError = false, isLoading = false }) {
  const [attempt, setAttempt] = useState(0);
  const [retryExceeded, setRetryExceeded] = useState(false);
  const [autoRetryExceeded, setAutoRetryExceeded] = useState(!auto);

  // reset auto retry, for the interval change scenario
  useEffect(() => {
    setAutoRetryExceeded(!auto);
  }, [auto]);

  // allow retry until limit
  const retryRequest = useCallback(() => {
    if (attempt < RETRY_LIMIT) {
      setAttempt((att) => att + 1);
      fetchData();
      logEvent();
    } else {
      setRetryExceeded(true);
    }
  }, [attempt, fetchData]);

  // auto retry
  const autoRetryRequest = useCallback(() => {
    const timeout = setTimeout(() => {
      retryRequest();
      // inclusive of the current attempt
      if (attempt + 1 >= AUTO_RETRY_LIMIT) {
        // to ensure loading state (dispatch/query) is set before retry state
        setTimeout(() => {
          setAutoRetryExceeded(true);
        }, []);
      }
    }, getAutoRetryDelay(attempt));
    return timeout;
  }, [attempt, retryRequest]);

  // support event for parent
  const handleRedirect = useCallback(() => {
    window.top.postMessage({ type: 'customer support' }, '*');
  }, []);

  // auto retry if component has a loading error
  useEffect(() => {
    let timeout;
    if (!autoRetryExceeded && hasError) {
      timeout = autoRetryRequest();
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [autoRetryExceeded, hasError, autoRetryRequest]);

  // reset on success
  useEffect(() => {
    if (!isLoading && !hasError && attempt > 0) {
      setAttempt(0);
      setRetryExceeded(false);
      setAutoRetryExceeded(!auto);
    }
  }, [hasError, isLoading, attempt]);

  return {
    retryExceeded,
    // in auto mode, to stall while a retry is being initialized
    retryInProgress: !autoRetryExceeded && hasError,
    retryRequest,
    handleRedirect,
  };
}

export default useRetry;
